// @flow
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import { History, Location } from "history";
import { withRouter } from "react-router";
import { Dispatch } from "react-redux";
import QueryMatch from "containers/QueryMatch";
import produces from "immer";

import ArchiveTargetList from "components/ArchiveTargets/ArchiveTargetList";
import ArchiveTargetHomeCards from "components/ArchiveTargets/ArchiveTargetList/ArchiveTargetHomeCards";
import ArchiveTargetSearchFormCards from "components/ArchiveTargets/ArchiveTargetList/ArchiveTargetSearchFormCards";
import ArchiveTargetSearchForm from "components/ArchiveTargets/ArchiveTargetSearchForm";
import ArchiveTargetSearchItem from "components/ArchiveTargets/ArchiveTargetSearchItem";
import { openPickerModal, PickerType } from "components/PickerModal";
import { type ApiError, CardType, CardItemType } from "data/types";
import { getArchiveTargets /*, submitArchiveForm*/ } from "data/api";
import { getLocalizedText } from "data/utils";
import Constants, { type ConstantsEnum } from "data/constants";
import Status from "components/Status";
import NoData from "components/NoData";
import { globalSitesAreaSelector } from "data/storeHelper";
import * as s from "data/reducers/selectors";
import loc from "i18next";

type Props = {
    /** whether to show list of archive targets */
    show: boolean,
    /** callback when target found
     * @param {string} targetItemUri target's itemUri to archive onto
     */
    onTargetItemUri: (targetItemUri: string) => void,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** React-Router history object */
    history: History,
    /** redux isOnline */
    isOnline: boolean,
    location: Location,
};

type State = {
    status: ConstantsEnum,
    targetItemUri: ?string,
    archiveTargets: ?Array<CardItemResultDTO>,
};

export class ArchiveTargetListContainer extends PureComponent<Props, State> {
    state = {
        targetItemUri: null,
        status: Constants.LOADING,
        archiveTargets: null,
    };

    componentDidMount = () => this._fetch();

    componentDidUpdate(prevProps: Props) {
        if (this.props.isOnline !== prevProps.isOnline) {
            this._fetch();
        }
    }

    _fetch = () => {
        this.setState({ status: Constants.LOADING });
        getArchiveTargets(!this.props.isOnline)
            .catch((err: ApiError) => {
                this.setState({ status: Constants.ERROR });
                return null;
            })
            .then((result) => {
                if (result != null) {
                    // TODO only one archiveForm -> preselect it - as long as we do not have suggestions, search, browse...
                    this.setState({
                        status: Constants.OK,
                        archiveTargets: result.map((card) =>
                            produces(card, (draft) => {
                                draft["name"] = getLocalizedText(draft["name"]);
                                draft["desc"] = getLocalizedText(draft["desc"]);
                            })
                        ),
                    });
                }
            });
    };

    _setTargetItemUri = (targetItemUri: string) => {
        /* istanbul ignore else */
        if (targetItemUri != null) {
            /* istanbul ignore else */
            if (typeof this.props.onTargetItemUri === "function") {
                this.props.onTargetItemUri(targetItemUri);
            }
        }
    };

    _onCardClick = (cardItem: CardItemDTO) => {
        if (cardItem.itemtype === CardItemType.Search) {
            this.props.history.push("/upload?mode=si", {
                searchItem: cardItem.searchItem,
            });
        } else {
            // direct folder itemUri, use it!
            this._setTargetItemUri(cardItem.uri);
        }
    };

    _onSearchFormClick = (cardItem: CardItemDTO) =>
        this.props.history.push(
            `/upload?mode=sfid&sfid=${cardItem.searchItem.id}`
        );

    _onCancel = () => this.props.history.push("/upload");

    render() {
        // #1.....list ready?
        if (this.props.show === false) {
            return null;
        }

        // fetch pending...
        if (this.state.archiveTargets == null) {
            return <Status status={this.state.status} />;
        }

        // no configured archiveTargets
        if (this.state.archiveTargets.length === 0) {
            return <NoData locContext="archivetarget" />;
        }

        return (
            <QueryMatch location={this.props.location}>
                {/* show list of archiveTargets */}
                <QueryMatch.Route
                    render={() => (
                        <ArchiveTargetList
                            archiveTargets={this.state.archiveTargets}
                        />
                    )}
                />
                {/* Recents */}
                <QueryMatch.Route
                    query={{ mode: "at-rec", id: null }}
                    render={(params) => (
                        <ArchiveTargetHomeCards
                            cardType={CardType.Rec}
                            archiveTarget={this.state.archiveTargets[params.id]}
                            onClick={this._onCardClick}
                            onCancel={this._onCancel}
                        />
                    )}
                />
                {/* Favorites */}
                <QueryMatch.Route
                    query={{ mode: "at-fav", id: null }}
                    render={(params) => (
                        <ArchiveTargetHomeCards
                            cardType={CardType.Fav}
                            archiveTarget={this.state.archiveTargets[params.id]}
                            onClick={this._onCardClick}
                            onCancel={this._onCancel}
                        />
                    )}
                />
                {/* List searchForms */}
                <QueryMatch.Route
                    query={{ mode: "at-search", id: null }}
                    render={(params) => (
                        <ArchiveTargetSearchFormCards
                            archiveTarget={this.state.archiveTargets[params.id]}
                            onClick={this._onSearchFormClick}
                            onCancel={this._onCancel}
                        />
                    )}
                />
                {/* when a searchForm is directly configured as a ArchiveTarget with searchType=FreeText */}
                <QueryMatch.Route
                    query={{ mode: "sf", id: null }}
                    render={(params) => (
                        <ArchiveTargetSearchForm
                            id={
                                this.state.archiveTargets[params.id].searchItem
                                    .id
                            }
                            onCancel={this._onCancel}
                            onTargetItemUri={this._setTargetItemUri}
                        />
                    )}
                />
                {/* when clicking a searchForm from within the searchForms list */}
                <QueryMatch.Route
                    query={{ mode: "sfid", sfid: null }}
                    render={(params) => (
                        <ArchiveTargetSearchForm
                            id={params.sfid}
                            onCancel={this._onCancel}
                            onTargetItemUri={this._setTargetItemUri}
                        />
                    )}
                />
                {/* when a searchForm is directly configured as a ArchiveTarget with searchType=Form and filters */}
                <QueryMatch.Route
                    query={{ mode: "sf-custom" }}
                    render={(params) => (
                        <ArchiveTargetSearchItem
                            searchItem={Object.assign(
                                {
                                    name: this.state.archiveTargets[params.id]
                                        .name,
                                },
                                this.state.archiveTargets[params.id].searchItem
                            )}
                            onCancel={this._onCancel}
                            onTargetItemUri={this._setTargetItemUri}
                        />
                    )}
                />
                {/* when clicking a Recent searchResult (=searchItem) */}
                <QueryMatch.Route
                    query={{ mode: "si" }}
                    render={() => {
                        if (this.props.location.state == null) {
                            // missing state - make sure to return to "base" if user refreshed browser
                            this._onCancel();
                            return <Status status={Constants.LOADING} />;
                        } else {
                            return (
                                <ArchiveTargetSearchItem
                                    searchItem={
                                        this.props.location.state.searchItem
                                    }
                                    onCancel={this._onCancel}
                                    onTargetItemUri={this._setTargetItemUri}
                                />
                            );
                        }
                    }}
                />
                {/* when a folder's itemUri is directly configured as a ArchiveTarget */}
                <QueryMatch.Route
                    query={{ mode: "uri", id: null }}
                    render={(params) => {
                        const archiveTarget =
                            this.state.archiveTargets[params.id];
                        /* istanbul ignore else */
                        if (String(archiveTarget.uri).startsWith("pam-item")) {
                            // make sure to return to "base" if user cancelled form submission
                            this._onCancel();
                            // let parent know they can now move forward
                            this._setTargetItemUri(archiveTarget.uri);
                        }

                        return <Status status={Constants.LOADING} />;
                    }}
                />
                {/* when a custom itemUri is passed by the picker */}
                <QueryMatch.Route
                    query={{ mode: "uri-custom", uri: null }}
                    render={(params) => {
                        // istanbul ignore else
                        if (String(params.uri).startsWith("pam-item")) {
                            // make sure to return to "base" if user cancelled form submission
                            this._onCancel();
                            // let parent know they can now move forward
                            this._setTargetItemUri(params.uri);
                        }

                        return <Status status={Constants.LOADING} />;
                    }}
                />

                {/* show Picker to choose target */}
                <QueryMatch.Route
                    query={{ mode: "at-picker", id: null }}
                    render={(params) => {
                        // find GlobalSites root itemUri
                        const globalSites = globalSitesAreaSelector();
                        /* istanbul ignore next */
                        if (globalSites == null) {
                            console.warn(
                                "Could not figure out root GlobalSites for picker!"
                            );
                            // make sure to return to "base" on error
                            this._onCancel();
                            return <Status status={Constants.ERROR} />;
                        }
                        // let user pick folder or doc
                        openPickerModal({
                            itemUri: globalSites.itemUri,
                            pickerType: PickerType.Folder,
                            helpText: loc.t("upload:at-picker.description"),
                        }).then((itemUri) => {
                            // user clicked Cancel? Abort
                            // make sure to return to "base" if user cancelled form submission
                            this._onCancel();
                            /* istanbul ignore else */
                            if (itemUri !== false)
                                // let parent know they can now move forward
                                this._setTargetItemUri(itemUri);
                        });
                        return <Status status={Constants.LOADING} />;
                    }}
                />
            </QueryMatch>
        );
    }
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    isOnline: s.isOnlineSelector(state),
    ...ownProps,
});

export default withRouter(connect(mapStateToProps)(ArchiveTargetListContainer));
